import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

// Вид одного тоста. Очередь и провайдер — hooks/useToast.tsx.

export type ToastTone = "ok" | "err" | "warn" | "info";
export type ToastAction = { label: string; run: () => void };
export type ToastItem = {
  id: number;
  tone: ToastTone;
  text: string;
  action?: ToastAction;
};

const ICON = {
  ok: CheckCircle2,
  err: XCircle,
  warn: AlertTriangle,
  info: Info,
} as const;

const COLOR: Record<ToastTone, string> = {
  ok: "text-emerald-600 dark:text-emerald-400",
  err: "text-rose-600 dark:text-rose-400",
  warn: "text-amber-600 dark:text-amber-400",
  info: "text-brand-600 dark:text-brand-300",
};

export function Toast({
  item,
  onClose,
}: {
  item: ToastItem;
  onClose: () => void;
}) {
  const Icon = ICON[item.tone];
  return (
    <div
      role="status"
      className="popover flex items-start gap-2 px-3 py-2 text-sm w-80 max-w-[calc(100vw-2rem)] animate-slide-up pointer-events-auto"
    >
      <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", COLOR[item.tone])} />
      <div className="flex-1 min-w-0 break-words">{item.text}</div>
      {item.action && (
        <button
          type="button"
          onClick={() => {
            onClose();
            item.action!.run();
          }}
          className="shrink-0 text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
        >
          {item.action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Стек тостов в правом нижнем углу. */
export function ToastStack({
  items,
  onClose,
}: {
  items: ToastItem[];
  onClose: (id: number) => void;
}) {
  if (!items.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none">
      {items.map((t) => (
        <Toast key={t.id} item={t} onClose={() => onClose(t.id)} />
      ))}
    </div>
  );
}

export default Toast;
